import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatTime, shiftStatusColors, formatStatus } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, 
  Plus, 
  Clock,
  MapPin,
  User,
  LogIn,
  LogOut,
  Coffee,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Navigation,
  QrCode
} from 'lucide-react';
import { QRScanner } from '../components/QRScanner';

export default function ShiftsPage() {
  const { api, user, isAdmin, isOfficer, isSupervisor } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [sites, setSites] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(isOfficer && !isAdmin ? 'my' : 'all');
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrVerified, setQrVerified] = useState(false);

  const [formData, setFormData] = useState({
  site_id: '',
  officer_id: '',
  start_date: '',
  start_time: '',
  end_date: '',
  end_time: '',
  notes: '',
});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const promises = [
        api.get('/shifts?today_only=true&limit=100'),
        api.get('/sites')
      ];
      
      if (isAdmin) {
        promises.push(api.get('/users?roles=security_officer,supervisor&is_active=true'));
        promises.push(api.get('/shifts/stats/attendance-today'));
      }
      
      if (isOfficer) {
        promises.push(api.get('/shifts/my-shifts'));
      }
      
      const results = await Promise.all(promises);
      
      setShifts(results[0].data);
      setSites(results[1].data);
      
      let idx = 2;
      if (isAdmin) {
        setOfficers(results[idx]?.data || []);
        idx++;
        setAttendanceStats(results[idx]?.data || null);
        idx++;
      }
      
      if (isOfficer) {
        setMyShifts(results[idx]?.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load shift data');
    } finally {
      setLoading(false);
    }
  }, [api, isAdmin, isOfficer]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateShift = async (e) => {
  e.preventDefault();

  if (!formData.site_id) {
    toast.error('Please select a site');
    return;
  }

  if (!formData.start_date || !formData.start_time || !formData.end_date || !formData.end_time) {
    toast.error('Please complete all date and time fields');
    return;
  }

  let finalEndDate = formData.end_date;

  // Handle overnight shifts
  if (formData.start_date === formData.end_date && formData.end_time < formData.start_time) {
    const nextDay = new Date(formData.end_date);
    nextDay.setDate(nextDay.getDate() + 1);
    finalEndDate = nextDay.toISOString().split('T')[0];
  }

  try {
    const params = new URLSearchParams();
    params.append('site_id', formData.site_id);
    params.append('start_time', new Date(`${formData.start_date}T${formData.start_time}:00`).toISOString());
    params.append('end_time', new Date(`${finalEndDate}T${formData.end_time}:00`).toISOString());
    if (formData.officer_id) params.append('officer_id', formData.officer_id);
    if (formData.notes) params.append('notes', formData.notes);

      await api.post(`/shifts?${params.toString()}`);
      toast.success('Shift created successfully');
      fetchData();
      setShowCreateDialog(false);
      setFormData({
        site_id: '',
        officer_id: '',
        start_time: '',
        end_time: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  const requestGPS = () => {
    setGpsLoading(true);
    setGpsError(null);
    
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser');
      setGpsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGpsLoading(false);
      },
      (error) => {
        let errorMsg = 'Unable to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out';
            break;
          default:
            errorMsg = 'An unknown error occurred';
        }
        setGpsError(errorMsg);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const handleCheckIn = async (shiftId, checkType = 'CHECK_IN') => {
    try {
      setCheckInLoading(shiftId);
      
      const params = new URLSearchParams();
      params.append('check_type', checkType);
      if (gpsLocation) {
        params.append('latitude', gpsLocation.latitude);
        params.append('longitude', gpsLocation.longitude);
      }
      if (checkInNotes) {
        params.append('notes', checkInNotes);
      }

      const response = await api.post(`/shifts/${shiftId}/check-in?${params.toString()}`);
      
      const message = checkType === 'CHECK_IN' ? 'Checked in successfully' : 'Checked out successfully';
      if (response.data.is_exception) {
        toast.warning(`${message} (Exception: ${response.data.exception_reason})`);
      } else {
        toast.success(message);
      }
      
      setSelectedShift(null);
      setCheckInNotes('');
      setGpsLocation(null);
      fetchData();
    } catch (error) {
      console.error('Error with check in/out:', error);
      toast.error(error.response?.data?.detail || 'Failed to process check in/out');
    } finally {
      setCheckInLoading(null);
    }
  };

  const openCheckInDialog = (shift, checkType) => {
    setSelectedShift({ ...shift, checkType });
    setCheckInNotes('');
    setGpsLocation(null);
    setGpsError(null);
    setQrVerified(false);
  };

  const handleQRScan = async (scannedData) => {
    try {
      // Verify the QR code
      const response = await api.post('/qr/verify', { token: scannedData });
      
      if (response.data.valid && response.data.type === 'site') {
        // Check if the scanned site matches the shift's site
        if (response.data.entity_id === selectedShift?.site_id) {
          setQrVerified(true);
          setShowQRScanner(false);
          toast.success(`QR verified: ${response.data.entity_name}`);
        } else {
          toast.error('QR code does not match this shift\'s site');
          setShowQRScanner(false);
        }
      } else {
        toast.error('Invalid QR code for check-in');
        setShowQRScanner(false);
      }
    } catch (error) {
      console.error('QR verification error:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify QR code');
      setShowQRScanner(false);
    }
  };

  const handleCheckInWithQR = async (shiftId, checkType) => {
    try {
      setCheckInLoading(shiftId);
      
      const params = new URLSearchParams();
      params.append('check_type', checkType);
      params.append('verification_method', qrVerified ? 'qr_code' : 'gps');
      if (gpsLocation) {
        params.append('latitude', gpsLocation.latitude);
        params.append('longitude', gpsLocation.longitude);
      }
      if (checkInNotes) {
        params.append('notes', checkInNotes);
      }

      const response = await api.post(`/shifts/${shiftId}/check-in?${params.toString()}`);
      
      const method = qrVerified ? ' (QR verified)' : (gpsLocation ? ' (GPS verified)' : '');
      
      // CHECK OUT - show report actions
      if (checkType === 'CHECK_OUT') {
        // Show timesheet info if available
        const timesheetInfo = response.data.timesheet 
          ? ` | Total: ${response.data.timesheet.total_hours}h` 
          : '';
        
        toast.success(
          <div className="space-y-2">
            <p>Checked out successfully{method}{timesheetInfo}</p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => {
                  window.location.href = `/reports?tab=shifts&view=${shiftId}`;
                }}
                className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
              >
                View Report
              </button>
              <button
                onClick={async () => {
                  try {
                    const pdfRes = await api.get(`/reports/shift/${shiftId}/pdf`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `shift_report_${shiftId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (e) {
                    console.error('PDF download error:', e);
                  }
                }}
                className="px-3 py-1 bg-zinc-600 text-white text-xs rounded hover:bg-zinc-500"
              >
                Download PDF
              </button>
            </div>
          </div>,
          { duration: 10000 }
        );
      } else {
        // CHECK IN - P0.3: Show geofence verification info
        const message = 'Checked in successfully';
        const geofenceInfo = response.data.geofence 
          ? ` (${response.data.geofence.distance}m from site)` 
          : '';
        
        // Show timesheet creation confirmation
        const timesheetCreated = response.data.timesheet?.action === 'created' 
          ? ' | Timesheet created' 
          : '';
        
        if (response.data.is_exception) {
          toast.warning(`${message}${method}${geofenceInfo}${timesheetCreated} (Exception: ${response.data.exception_reason})`);
        } else {
          toast.success(`${message}${method}${geofenceInfo}${timesheetCreated}`);
        }
      }
      
      setSelectedShift(null);
      setCheckInNotes('');
      setGpsLocation(null);
      setQrVerified(false);
      fetchData();

      // P1 STATIC SHIFT FLOW FIX
      // Static guarding shifts should open Daily Activity Log,
      // not patrol route workflows.
      if (checkType === 'CHECK_IN') {

        const shiftType =
          selectedShift?.shift_type ||
          selectedShift?.type ||
          selectedShift?.site_type ||
          '';

        const isStaticShift =
          String(shiftType).toLowerCase().includes('static');

        setTimeout(() => {

          // STATIC SHIFT → DAILY ACTIVITY LOG
          if (isStaticShift) {
            window.location.href = `/daily-activity-log?shift_id=${selectedShift?.id || selectedShift?._id}`;
            return;
          }

          // PATROL / MOBILE SHIFT → MY ROUTES
          window.location.href = '/my-routes';

        }, 800);
      }
    } catch (error) {
      console.error('Error with check in/out:', error);
      
      // P0.3: Enhanced geofence error handling - show clear distance-based message
      const errorDetail = error.response?.data?.detail || '';
      
      // Check if this is a geofence error (contains distance info)
      if (errorDetail.includes('away from') || errorDetail.includes('geofence') || errorDetail.includes('outside')) {
        toast.error(
          <div className="space-y-2">
            <p className="font-semibold text-red-400">Clock-in Blocked - Outside Geofence</p>
            <p className="text-sm">{errorDetail}</p>
            <p className="text-xs text-zinc-400 mt-2">Please move closer to the site location and try again.</p>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorDetail || 'Failed to process check in/out');
      }
    } finally {
      setCheckInLoading(null);
    }
  };

  const displayShifts = activeTab === 'my' ? myShifts : shifts;

  return (
    <div className="space-y-6" data-testid="shifts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            ROSTER & SHIFTS
          </h1>
          <p className="text-sm text-zinc-500">Manage shift schedules and attendance</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
          
          {isAdmin && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-rajdhani font-semibold rounded-sm shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                  data-testid="create-shift-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  NEW SHIFT
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-rajdhani text-xl">CREATE SHIFT</DialogTitle>
                  <DialogDescription className="text-zinc-500">
                    Schedule a new shift assignment.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateShift} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Site <span className="text-red-400">*</span></Label>
                    <Select 
                      value={formData.site_id} 
                      onValueChange={(v) => setFormData({...formData, site_id: v})}
                    >
                      <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="shift-site-select">
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                        {sites.map(site => (
                          <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Assign Officer</Label>
                    <Select 
                      value={formData.officer_id} 
                      onValueChange={(v) => setFormData({...formData, officer_id: v})}
                    >
                      <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="shift-officer-select">
                        <SelectValue placeholder="Select officer (optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                        {officers.map(officer => (
                          <SelectItem key={officer.id} value={officer.id}>
                            {officer.first_name} {officer.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
  <div className="space-y-2">
    <Label className="text-zinc-300">Start Date <span className="text-red-400">*</span></Label>
    <Input
      type="date"
      value={formData.start_date || ''}
      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
      className="bg-[#09090b] border-[#3f3f46]"
      required
      data-testid="shift-start-date"
    />
  </div>

  <div className="space-y-2">
    <Label className="text-zinc-300">Start Time <span className="text-red-400">*</span></Label>
    <Input
      type="time"
      value={formData.start_time || ''}
      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
      className="bg-[#09090b] border-[#3f3f46]"
      required
      step="60"
      data-testid="shift-start-time"
    />
  </div>

  <div className="space-y-2">
    <Label className="text-zinc-300">End Date <span className="text-red-400">*</span></Label>
    <Input
      type="date"
      value={formData.end_date || ''}
      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
      className="bg-[#09090b] border-[#3f3f46]"
      required
      data-testid="shift-end-date"
    />
  </div>

  <div className="space-y-2">
    <Label className="text-zinc-300">End Time <span className="text-red-400">*</span></Label>
    <Input
      type="time"
      value={formData.end_time || ''}
      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
      className="bg-[#09090b] border-[#3f3f46]"
      required
      step="60"
      data-testid="shift-end-time"
    />
  </div>
</div>

<div className="space-y-2">
  <Label className="text-zinc-300">Notes</Label>
  <Textarea
    value={formData.notes}
    onChange={(e) => setFormData({...formData, notes: e.target.value})}
    placeholder="Any special instructions..."
    className="bg-[#09090b] border-[#3f3f46]"
  />
</div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowCreateDialog(false)}
                      className="border-zinc-600"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-blue-500 hover:bg-blue-600"
                      data-testid="submit-shift-btn"
                    >
                      Create Shift
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Attendance Stats (Admin only) */}
      {isAdmin && attendanceStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Calendar className="w-5 h-5 text-blue-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {attendanceStats.total_shifts || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Total Shifts</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-2xl font-mono font-bold text-emerald-400">
                  {attendanceStats.on_duty || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">On Duty</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="w-5 h-5 text-zinc-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {attendanceStats.scheduled || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Scheduled</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className={`text-2xl font-mono font-bold ${attendanceStats.late_arrivals > 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                  {attendanceStats.late_arrivals || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Late Arrivals</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className={`text-2xl font-mono font-bold ${attendanceStats.no_show > 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                  {attendanceStats.no_show || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">No Shows</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Officer View */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#27272a] border-b border-[#3f3f46] w-full justify-start rounded-none h-auto p-0">
          {isOfficer && (
            <TabsTrigger 
              value="my" 
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 px-4 py-2"
              data-testid="my-shifts-tab"
            >
              <User className="w-4 h-4 mr-2" />
              My Shifts
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="all" 
            className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 px-4 py-2"
            data-testid="all-shifts-tab"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Today's Schedule
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {/* Shifts List */}
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
              <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                {activeTab === 'my' ? 'MY SHIFTS' : "TODAY'S SHIFTS"} ({displayShifts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                </div>
              ) : displayShifts.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No shifts scheduled</p>
                </div>
              ) : (
                <div className="divide-y divide-[#3f3f46]">
                  {displayShifts.map((shift) => {
                    const isMyShift = shift.officer_id === user?.id;

                    const shiftStatus = String(
                      shift.status || ''
                    ).toLowerCase();

                    const acceptanceStatus = String(
                      shift.acceptance_status || ''
                    ).toLowerCase();

                    const canAcceptShift =
                      isMyShift &&
                      ['scheduled', 'pending_acceptance', 'unaccepted'].includes(acceptanceStatus);

                    const canCheckIn =
                      isMyShift &&
                      acceptanceStatus === 'accepted';


                    const canCheckOut =
                      isMyShift &&
                      shift.status?.toLowerCase() === 'in_progress';
                    
                    return (
                      <div 
                        key={shift.id}
                        className="p-4 hover:bg-[#27272a]/50 transition-colors"
                        data-testid={`shift-item-${shift.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={`${shiftStatusColors[shift.status?.toLowerCase()]} text-xs px-1.5 py-0 rounded-sm border`}>
                                {formatStatus(
  acceptanceStatus === 'accepted'
    ? 'accepted'
    : shift.status
)}
                              </Badge>
                              {isMyShift && (
                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50 text-xs px-1.5 py-0 rounded-sm border">
                                  Your Shift
                                </Badge>
                              )}
                            </div>
                            
                            <div className="space-y-1 text-sm text-zinc-300 mb-2">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                <span className="font-medium">{shift.site_name || 'Unknown Site'}</span>
                                {shift.site_code && (
                                  <span className="text-zinc-500 font-mono text-xs">({shift.site_code})</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                <span>{shift.officer_name || (isMyShift ? 'Assigned To You' : 'Unassigned')}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                              </span>
                              {shift.actual_start && (
                                <span className="text-emerald-400 flex items-center gap-1">
                                  <LogIn className="w-3 h-3" />
                                  In: {formatTime(shift.actual_start)}
                                </span>
                              )}
                              {shift.actual_end && (
                                <span className="text-emerald-400 flex items-center gap-1">
                                  <LogOut className="w-3 h-3" />
                                  Out: {formatTime(shift.actual_end)}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons - Mobile Friendly */}
                          {isMyShift && (canAcceptShift || canCheckIn || canCheckOut) && (
                            <div className="flex gap-2 sm:flex-col w-full sm:w-auto">

                              {canAcceptShift && (
                                <>
                                  <Button
                                    className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
                                    onClick={async () => {
                                      try {

                                        // ACCEPT SHIFT
                                        await api.post(`/roster/shifts/${shift.id}/accept`);

                                        toast.success('Shift accepted');

                                        // REFRESH SEPARATELY
                                        try {
                                          await fetchData();
                                        } catch (refreshErr) {
                                          console.error('Shift refresh failed:', refreshErr);
                                        }

                                      } catch (e) {

                                        const msg =
                                          e?.response?.data?.detail ||
                                          e?.response?.data?.message ||
                                          '';

                                        // Already accepted = SUCCESS
                                        if (
                                          String(msg)
                                            .toLowerCase()
                                            .includes('accepted status')
                                        ) {

                                          toast.success('Shift already accepted');

                                          try {
                                            await fetchData();
                                          } catch (refreshErr) {
                                            console.error('Shift refresh failed:', refreshErr);
                                          }

                                          return;
                                        }

                                        toast.error(msg || 'Failed to accept shift');
                                      }
                                    }}
                                  >
                                    Accept
                                  </Button>

                                  <Button
                                    className="bg-red-600 hover:bg-red-700 flex-1 sm:flex-none"
                                    onClick={async () => {
                                      try {
                                        await api.post(`/roster/shifts/${shift.id}/decline?reason=Rejected by officer`);
                                        toast.success('Shift rejected');
                                        fetchShifts();
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Failed to reject shift');
                                      }
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}

                              {canCheckIn && (
                                <Button
                                  className="bg-emerald-500 hover:bg-emerald-600 flex-1 sm:flex-none"
                                  onClick={() => openCheckInDialog(shift, 'CHECK_IN')}
                                  disabled={checkInLoading === shift.id}
                                  data-testid={`check-in-btn-${shift.id}`}
                                >
                                  {checkInLoading === shift.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <LogIn className="w-4 h-4 mr-2" />
                                  )}
                                  Check In
                                </Button>
                              )}
                              {canCheckOut && (
                                <Button
                                  className="bg-orange-500 hover:bg-orange-600 flex-1 sm:flex-none"
                                  onClick={() => openCheckInDialog(shift, 'CHECK_OUT')}
                                  disabled={checkInLoading === shift.id}
                                  data-testid={`check-out-btn-${shift.id}`}
                                >
                                  {checkInLoading === shift.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <LogOut className="w-4 h-4 mr-2" />
                                  )}
                                  Check Out
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Check In/Out Dialog */}
      <Dialog open={!!selectedShift} onOpenChange={() => setSelectedShift(null)}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              {selectedShift?.checkType === 'CHECK_IN' ? (
                <>
                  <LogIn className="w-5 h-5 text-emerald-400" />
                  Check In
                </>
              ) : (
                <>
                  <LogOut className="w-5 h-5 text-orange-400" />
                  Check Out
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {selectedShift?.site_name} • {formatTime(selectedShift?.start_time)} - {formatTime(selectedShift?.end_time)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* P0.3: Geofence Check-In Notice */}
            {selectedShift?.checkType === 'CHECK_IN' && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                <div className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-400 font-medium text-sm">Geofence Verification Required</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Clock-in requires being within 200m of the site location. 
                      Your GPS position will be verified against the site coordinates.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Verification Method */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Location Verification</Label>
              
              {/* QR Verified Badge */}
              {qrVerified && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">QR Code Verified</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
                </div>
              )}
              
              {/* GPS Location */}
              {!qrVerified && (
                <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46]">
                  {gpsLocation ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-emerald-400">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          GPS location captured
                        </span>
                      </div>
                      <div className="text-xs text-zinc-400 space-y-1 pl-6">
                        <p>Accuracy: ±{Math.round(gpsLocation.accuracy)}m</p>
                        <p>Lat: {gpsLocation.latitude.toFixed(6)}, Lng: {gpsLocation.longitude.toFixed(6)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={requestGPS}
                        disabled={gpsLoading}
                        className="border-zinc-600 text-xs mt-2"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Refresh Location
                      </Button>
                    </div>
                  ) : gpsError ? (
                    <div className="flex items-center gap-2 text-yellow-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm">{gpsError}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={requestGPS}
                          disabled={gpsLoading}
                          className="border-zinc-600 flex-1"
                        >
                          {gpsLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Navigation className="w-4 h-4 mr-2" />
                          )}
                          {gpsLoading ? 'Getting...' : 'Use GPS'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowQRScanner(true)}
                          className="border-zinc-600 flex-1"
                        >
                          <QrCode className="w-4 h-4 mr-2" />
                          Scan QR
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-zinc-500">
                Use GPS or scan the site QR code to verify your location.
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes (optional)</Label>
              <Textarea
                value={checkInNotes}
                onChange={(e) => setCheckInNotes(e.target.value)}
                placeholder="Any notes for this check in/out..."
                className="bg-[#09090b] border-[#3f3f46]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setSelectedShift(null)}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleCheckInWithQR(selectedShift?.id, selectedShift?.checkType)}
              className={selectedShift?.checkType === 'CHECK_IN' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-orange-500 hover:bg-orange-600'}
              disabled={checkInLoading === selectedShift?.id}
              data-testid="confirm-check-btn"
            >
              {checkInLoading === selectedShift?.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : selectedShift?.checkType === 'CHECK_IN' ? (
                <LogIn className="w-4 h-4 mr-2" />
              ) : (
                <LogOut className="w-4 h-4 mr-2" />
              )}
              Confirm {selectedShift?.checkType === 'CHECK_IN' ? 'Check In' : 'Check Out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <QRScanner
        open={showQRScanner}
        onOpenChange={setShowQRScanner}
        onScanSuccess={handleQRScan}
        title="Scan Site QR Code"
        description="Scan the QR code at your assigned site to verify your location"
      />
    </div>
  );
}
